import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { useDive } from './DiveContext';
import './DiveOverlay.css';

const BUBBLES = [
  { id: 0,  left: 12, bottom: 8,  size: 7  },
  { id: 1,  left: 24, bottom: 15, size: 4  },
  { id: 2,  left: 38, bottom: 6,  size: 10 },
  { id: 3,  left: 51, bottom: 20, size: 5  },
  { id: 4,  left: 64, bottom: 10, size: 8  },
  { id: 5,  left: 77, bottom: 25, size: 4  },
  { id: 6,  left: 88, bottom: 14, size: 7  },
  { id: 7,  left: 19, bottom: 32, size: 6  },
  { id: 8,  left: 44, bottom: 28, size: 9  },
  { id: 9,  left: 70, bottom: 18, size: 5  },
  { id: 10, left:  6, bottom: 22, size: 8  },
  { id: 11, left: 92, bottom: 30, size: 6  },
];

export default function DiveOverlay() {
  const { phase, notifyCovered, notifyDone } = useDive();
  const overlayRef  = useRef(null);
  const vignetteRef = useRef(null);
  const deepRef     = useRef(null);

  useEffect(() => {
    if (phase !== 'diving') return;
    const overlay  = overlayRef.current;
    const vignette = vignetteRef.current;
    const deep     = deepRef.current;
    if (!overlay || !vignette || !deep) return;

    gsap.set(overlay,  { autoAlpha: 1 });
    gsap.set(vignette, { opacity: 0 });
    gsap.set(deep,     { autoAlpha: 0 });
    gsap.set('.dive-bubble', { yPercent: 0, autoAlpha: 0, scale: 0.6 });
    gsap.set('.dive-loader', { autoAlpha: 0 });

    const tl = gsap.timeline({ onComplete: notifyDone });

    // FASE 1: MERGULHO (0 → 1.4s) — zoom acelera, tela escurece até preto total
    tl.to('.login-page', { scale: 1.05, duration: 0.4, ease: 'power1.out', transformOrigin: 'center center' }, 0);
    tl.to(vignette,      { opacity: 0.20, duration: 0.4, ease: 'power1.out' }, 0);
    tl.to('.login-page', { scale: 1.55, filter: 'blur(8px)', duration: 1.0, ease: 'power3.in', transformOrigin: 'center center' }, 0.4);
    tl.to(vignette,      { opacity: 1.0, duration: 0.7, ease: 'power2.in' }, 0.7);
    // tela totalmente coberta: troca o conteúdo por baixo
    tl.add(() => { gsap.set('.login-page', { clearProps: 'all' }); notifyCovered(); }, 1.35);

    // FASE 2: ESCURIDÃO COM BOLHAS (1.4 → 3.0s)
    tl.set(deep,    { autoAlpha: 1 }, 1.4);
    tl.set(vignette, { opacity: 0 },  1.4);
    tl.to('.dive-loader', { autoAlpha: 1, duration: 0.5, ease: 'power1.out' }, 1.6);
    tl.to('.dive-bubble', { yPercent: -320, autoAlpha: 0.80, scale: 1, duration: 1.2, stagger: { each: 0.08, from: 'random' }, ease: 'power1.out' }, 1.5);
    tl.to('.dive-bubble', { autoAlpha: 0, duration: 0.4, stagger: { each: 0.04, from: 'start' }, ease: 'power1.in' }, 2.4);
    tl.to('.dive-loader', { autoAlpha: 0, duration: 0.4, ease: 'power1.in' }, 2.7);

    // FASE 3: REVELAÇÃO (3.0 → 4.8s) — escuridão dissolve, app aparece gradualmente
    tl.to(deep,    { autoAlpha: 0, duration: 1.5, ease: 'power1.inOut' }, 3.0);
    tl.to(overlay, { autoAlpha: 0, duration: 0.3, ease: 'power1.in'   }, 4.5);

    return () => {
      tl.kill();
      gsap.set(overlay, { autoAlpha: 0 });
      gsap.set('.login-page', { clearProps: 'all' });
    };
  }, [phase, notifyCovered, notifyDone]);

  return (
    <div ref={overlayRef} className="dive-overlay" aria-hidden="true">
      <div ref={vignetteRef} className="dive-vignette" />
      <div ref={deepRef} className="dive-deep">
        <div className="dive-bubbles">
          {BUBBLES.map((b) => (
            <div
              key={b.id}
              className="dive-bubble"
              style={{ left: b.left + '%', bottom: b.bottom + '%', width: b.size, height: b.size }}
            />
          ))}
        </div>
        <div className="dive-loader">
          <div className="dive-loader-ring" />
          <span className="dive-loader-text">Carregando</span>
        </div>
      </div>
    </div>
  );
}
